import React from 'react';

import { Button, Divider, Flex, Paper, Stack, Text, Title } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { ConversationHandoffContent } from 'pages/ManageSmartAssistant/Sections/ConversationSections/ConversationsSection/ConversationHeader/ConversationHandoffContent';
import { ConversationTakeoverContent } from 'pages/ManageSmartAssistant/Sections/ConversationSections/ConversationsSection/ConversationHeader/ConversationTakeoverContent';
import { CreateMemoryButton } from 'pages/ManageSmartAssistant/Sections/ConversationSections/CreateMemoryButton/CreateMemoryButton';
import { useTranslation } from 'react-i18next';
import { EvaluationTestInfo } from 'shared/constants/evaluation';
import { Conversation } from 'shared/interfaces/Conversation';
import { EvaluationTestStatus } from 'shared/interfaces/Evaluation';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { getReadableFormatDateTime } from 'shared/utils/date';

type TakeOverData = {
  canTakeOver?: boolean;
  isPastMetaTakeoverWindow: boolean;
};

interface ConversationHeaderProps {
  conversation: Conversation;
  smartAssistant: SmartAssistant;
  onConversationUpdate: (updatedConversation: Conversation) => void;
  evaluationTestInfo?: EvaluationTestInfo;
  takeOverData: TakeOverData;
  isGuest?: boolean;
}

export function ConversationHeader({
  conversation,
  smartAssistant,
  onConversationUpdate,
  evaluationTestInfo,
  takeOverData,
  isGuest = false
}: ConversationHeaderProps) {
  const { t } = useTranslation();

  const formattedDate = getReadableFormatDateTime(conversation.createdAt);

  return (
    <Paper withBorder sx={{ padding: '12px 18px' }}>
      <Flex direction="column" gap="xs">
        <Flex justify="space-between" align="center">
          <Flex align="center" gap="sm">
            <Title order={3} fw={600}>
              {t('SMART_ASSISTANT.CONVERSATIONS_SECTION.HEADER.TITLE')}
            </Title>
            <Text color="dimmed" size="sm">
              {formattedDate}
            </Text>
          </Flex>

          {!isGuest && (
            <Flex gap="xs" align="center">
              {evaluationTestInfo && (
                <Button
                  loading={evaluationTestInfo.evaluationTest.status === EvaluationTestStatus.RUNNING}
                  variant="subtle"
                  leftIcon={<IconRefresh />}
                  onClick={evaluationTestInfo.onRunEvaluationTest}>
                  {t('SMART_ASSISTANT.CONVERSATIONS_SECTION.EVALUATION.RERUN_TEST')}
                </Button>
              )}

              <CreateMemoryButton conversation={conversation} smartAssistant={smartAssistant} />
            </Flex>
          )}
        </Flex>

        {takeOverData.canTakeOver && !isGuest && (
          <Stack spacing="xs">
            <Divider color="gray.2" size="xs" />
            <ConversationTakeoverContent
              conversation={conversation}
              onConversationUpdate={onConversationUpdate}
              isPastMetaTakeoverWindow={takeOverData.isPastMetaTakeoverWindow}
            />
          </Stack>
        )}

        {conversation.handoffData && !isGuest && (
          <Stack spacing="xs">
            <Divider color="gray.2" size="xs" />
            <ConversationHandoffContent conversation={conversation} onConversationUpdate={onConversationUpdate} />
          </Stack>
        )}
      </Flex>
    </Paper>
  );
}
